import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useSetting } from "../settings/SettingsContext";
import type { AlertSeverity, ServiceAlert } from "../api/types";

const SEVERITIES: AlertSeverity[] = ["INFO", "WARNING", "SEVERE"];

const CAUSES = [
  "UNKNOWN_CAUSE",
  "TECHNICAL_PROBLEM",
  "ACCIDENT",
  "WEATHER",
  "STRIKE",
  "DEMONSTRATION",
  "MAINTENANCE",
  "CONSTRUCTION",
  "POLICE_ACTIVITY",
  "MEDICAL_EMERGENCY",
];

const EFFECTS = [
  "NO_SERVICE",
  "REDUCED_SERVICE",
  "SIGNIFICANT_DELAYS",
  "DETOUR",
  "MODIFIED_SERVICE",
  "STOP_MOVED",
  "OTHER_EFFECT",
];

function enumLabel(v?: string): string {
  if (!v) return "—";
  const s = v.toLowerCase().replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtAlertTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function severityTone(s: string): "info" | "warn" | "crit" {
  if (s === "INFO") return "info";
  if (s === "WARNING") return "warn";
  return "crit";
}

export default function AlertsPage() {
  const refreshMs = useSetting<number>("occ.alerts_refresh_ms", 10000);
  const [alerts, setAlerts] = useState<ServiceAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [headerText, setHeaderText] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<AlertSeverity>("WARNING");
  const [cause, setCause] = useState("UNKNOWN_CAUSE");
  const [effect, setEffect] = useState("SIGNIFICANT_DELAYS");
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | "ALL">("ALL");

  async function load() {
    try {
      const data = await api.listActiveAlerts();
      setAlerts(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load alerts");
    }
  }

  useEffect(() => {
    load();
    const id = window.setInterval(load, refreshMs);
    return () => window.clearInterval(id);
  }, [refreshMs]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!headerText.trim()) return;
    setCreating(true);
    try {
      await api.createAlert({
        alert_type: "SERVICE",
        cause,
        effect,
        severity,
        header_text: headerText.trim(),
        description_text: description.trim() || undefined,
        active_period_start: new Date().toISOString(),
      });
      setHeaderText("");
      setDescription("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create alert");
    } finally {
      setCreating(false);
    }
  }

  async function onDeactivate(id: string) {
    try {
      await api.deactivateAlert(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deactivate");
    }
  }

  const filteredAlerts = useMemo(
    () => (severityFilter === "ALL" ? alerts : alerts.filter((a) => a.severity === severityFilter)),
    [alerts, severityFilter]
  );

  return (
    <div className="page alerts-page">
      <div className="alerts-header">
        <div>
          <h1>Service Alerts</h1>
          <p className="secondary-text">Publish rider-facing alerts to the app and station displays.</p>
        </div>
      </div>

      <div className="alerts-hero-grid">
        <section className="card alerts-compose-card">
          <h3>Publish service alert</h3>
          <form onSubmit={onCreate}>
            <label className="alerts-field">
              <span>Header</span>
              <input
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                placeholder="Detour on Route 14"
                required
              />
            </label>

            <label className="alerts-field">
              <span>Severity</span>
              <div className="alerts-sev-toggle">
                {SEVERITIES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`${severity === s ? "active" : ""} ${s.toLowerCase()}`}
                    onClick={() => setSeverity(s)}
                  >
                    {enumLabel(s)}
                  </button>
                ))}
              </div>
            </label>

            <div className="alerts-form-row">
              <label className="alerts-field">
                <span>Cause</span>
                <select value={cause} onChange={(e) => setCause(e.target.value)}>
                  {CAUSES.map((c) => <option key={c} value={c}>{enumLabel(c)}</option>)}
                </select>
              </label>
              <label className="alerts-field">
                <span>Effect</span>
                <select value={effect} onChange={(e) => setEffect(e.target.value)}>
                  {EFFECTS.map((c) => <option key={c} value={c}>{enumLabel(c)}</option>)}
                </select>
              </label>
            </div>

            <label className="alerts-field">
              <span>Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Visible to riders in the app and on station displays..."
              />
            </label>

            {error && <div className="banner banner-error">{error}</div>}

            <div className="alerts-actions-row">
              <button className="btn primary alerts-publish-btn" type="submit" disabled={creating}>
                {creating ? "Publishing…" : "Publish alert"}
              </button>
            </div>
          </form>
        </section>

        <section className="card alerts-log-card">
          <div className="alerts-log-head">
            <h3>
              <span className="alerts-log-bar" />
              Active alerts
              <span className="alerts-count-pill">{alerts.length} ACTIVE</span>
            </h3>
            <div className="alerts-segmented">
              <button type="button" className={severityFilter === "ALL" ? "active" : ""} onClick={() => setSeverityFilter("ALL")}>All</button>
              <button type="button" className={severityFilter === "INFO" ? "active" : ""} onClick={() => setSeverityFilter("INFO")}>Info</button>
              <button type="button" className={severityFilter === "WARNING" ? "active" : ""} onClick={() => setSeverityFilter("WARNING")}>Warning</button>
              <button type="button" className={severityFilter === "SEVERE" ? "active" : ""} onClick={() => setSeverityFilter("SEVERE")}>Severe</button>
            </div>
          </div>

          <div className="alerts-list">
            {filteredAlerts.length === 0 && <div className="empty">— no active alerts —</div>}
            {filteredAlerts.map((a) => (
              <div className="alerts-row" key={a.id} title={a.description_text}>
                <span className={`alerts-sev-pill ${severityTone(a.severity)}`}>{a.severity}</span>
                <div className="alerts-row-main">
                  <div className="alerts-row-header">{a.header_text}</div>
                  <div className="alerts-row-sub">{enumLabel(a.cause)} → {enumLabel(a.effect)}</div>
                </div>
                <span className="alerts-row-time">{fmtAlertTime(a.active_period_start || a.created_at)}</span>
                <button className="btn ghost alerts-deactivate-btn" type="button" onClick={() => onDeactivate(a.id)}>
                  Deactivate
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
